import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Auth from '../../components/Auth/Auth';
import { BreadCrumb } from '../../routes/BreadCrumbs/ingresse.breadcrumb';
import useBreadcrumbSelector from '../useBreadcrumbSelector';
import { useEventContext } from './event.context';
import { useI18nContext } from './i18n.context';
import { useModalContext } from './modal.context';

export type BreadCrumbExtends = BreadCrumb & { active: boolean };

export type BreadCrumbsProps = {
    breadCrumbs: BreadCrumbExtends[];
    currentBreadCrumb: string;
    isPaymentPage: boolean;
    isTicketPage: boolean;
    isNamingPage: boolean;
    isConfirmationPage: boolean;
    isResalePage: boolean;
    isResalePaymentPage: boolean;
};

const BreadCrumbsContext = createContext({} as BreadCrumbsProps);

function BreadCrumbsProvider({ children }: { children: ReactNode }) {
  const [currentBreadCrumb, setCurrentBreadCrumb] = useState('');
  const [isPaymentPage, setIsPaymentPage] = useState(false);
  const [isTicketPage, setIsTicketPage] = useState(false);
  const [isNamingPage, setIsNamingPage] = useState(false);
  const [isConfirmationPage, setIsConfirmationPage] = useState(false);
  const [isResalePage, setIsResalePage] = useState(false);
  const [isResalePaymentPage, setIsResalePaymentPage] = useState(false);

  const { producerId } = useEventContext();
  const { translate } = useI18nContext();
  const { toggleModal, defineTitleModal, setContentModal } = useModalContext();
  const { breadCrumbs, activeRoute } = useBreadcrumbSelector(producerId);
  const location = useLocation();

  /**
   * useEffect hook to handle side effects when the active route changes.
   * This hook runs whenever the `activeRoute` changes and performs the following actions:
   * - Sets the `isPaymentPage` state to true if the active route is 'payment', otherwise sets it to false.
   * - Sets the `currentBreadCrumb` state to the current active route.
   * @param activeRoute - The current active route.
  */
  useEffect(() => {
    // Check if we're on resale payment page (/resale/:id/payment)
    const isResalePayment = location.pathname.match(/^\/resale\/[^/]+\/payment$/);

    setIsPaymentPage(activeRoute === 'payment');
    setIsTicketPage(activeRoute === 'tickets');
    setIsNamingPage(activeRoute === 'naming');
    setIsConfirmationPage(activeRoute === 'payment-confirmed');
    setIsResalePage(activeRoute === 'resale' && !isResalePayment);
    setIsResalePaymentPage(!!isResalePayment);
    setCurrentBreadCrumb(activeRoute);
  }, [activeRoute, location.pathname]);

  useEffect(() => {
    if(location?.state?.from) {
      const cachedRoute = location.state.from;
      setTimeout(() => {
        defineTitleModal(translate(['INFORMATION', 'IDENTIFY_YOURSELF']));
        setContentModal(<Auth cachedRoute={cachedRoute} path='login' />);
        toggleModal({ open : true });
      }, 1500);
    }
  }, [location]);

  const shared = useMemo(() => ({
    breadCrumbs,
    currentBreadCrumb,
    isPaymentPage,
    isTicketPage,
    isNamingPage,
    isConfirmationPage,
    isResalePage,
    isResalePaymentPage
  }), [
    breadCrumbs,
    currentBreadCrumb,
    isPaymentPage,
    isTicketPage,
    isNamingPage,
    isConfirmationPage,
    isResalePage,
    isResalePaymentPage
  ]);

  return (
    <BreadCrumbsContext.Provider value={shared}>
      {children}
    </BreadCrumbsContext.Provider>
  );
}

BreadCrumbsProvider.displayName = 'BreadcrumbProvider';
export const useBreadCrumbsContext = (): BreadCrumbsProps => useContext(BreadCrumbsContext);

export { BreadCrumbsContext, BreadCrumbsProvider };
